// All sim state is plain serializable data — classes exist only in the engine
// (Simulation, BoomRngV1, BoomClock). Quantities are integers everywhere the
// economy can see them: money in cents, ore in decitons (0.1 t).

export type Col = number; // y * MAP_W + x

export type OreType = 0 | 1 | 2;
export const ORE_NONE = 0 as const;
export const ORE_IRON = 1 as const;
export const ORE_GOLD = 2 as const;

export const FLAG_ROAD = 1;
export const FLAG_BUILDING = 2;
export const FLAG_POLE = 4;
export const FLAG_PIPE = 8;
export const FLAG_CABLE = 16;
/** Upgrade layered on FLAG_ROAD: stone paving, faster travel. */
export const FLAG_PAVED = 32;
/** Reserved rail right-of-way (row south of each depot, west to the map
 * edge). Blocks buildings and digging; roads may cross it. */
export const FLAG_RAIL = 64;
/** Reserved transmission corridor (row south of each grid interconnect, east
 * to the map edge). Same rules as rail. Last free bit — the next flag needs
 * a wider array. */
export const FLAG_POWER = 128;

export interface Terrain {
  /** Per column: number of blocks excavated below grade (0..MAX_DEPTH). */
  depth: Uint8Array;
  /** Per cell (z * COLS + col): OreType. */
  oreType: Uint8Array;
  /** Per cell: ore in decitons, 0..255. */
  oreAmount: Uint8Array;
  /** Per column bitfield: FLAG_ROAD | FLAG_BUILDING | FLAG_POLE | FLAG_PIPE. */
  flags: Uint8Array;
  /** Per column: 1 if the pit here was filled back to grade. Render/inspect
   * marker only — every rule (buildability, dust) runs off depth. */
  reclaimed: Uint8Array;
  /** Bumped on any dig/flag change; renderer and path caches watch this. */
  version: number;
}

export interface DigOrder {
  col: Col;
  targetDepth: number;
  claimedBy: number | null;
  suspended: boolean;
}

export interface OrePile {
  col: Col;
  ironDt: number;
  goldDt: number;
  /** Decitons already claimed by a hauler en route. */
  claimedDt: number;
}

/** Post-ore trade goods. Integer units everywhere; prices in cents/unit.
 * Order is load-bearing: the market drifts commodities in this order on the
 * shared RNG fork, so inserting mid-list reshuffles every price path. */
export type GoodKind = 'corn' | 'wheat' | 'cattle' | 'glass' | 'chips';
export const GOOD_KINDS: readonly GoodKind[] = ['corn', 'wheat', 'cattle', 'glass', 'chips'];

export interface GoodsPile {
  col: Col;
  good: GoodKind;
  units: number;
  /** Units already claimed by a hauler en route. */
  claimedUnits: number;
  /** No route found last attempt; cleared whenever the terrain changes, the
   * same contract as DigOrder.suspended. Keeps the scheduler from burning its
   * path budget re-failing a walled-in pile every pass. */
  suspended: boolean;
}

export type BuildingType =
  | 'hq'
  | 'trailer'
  | 'bunkhouse'
  | 'cottage'
  | 'familyHouse'
  | 'townhome'
  | 'apartment'
  | 'canteen'
  | 'generalStore'
  | 'saloon'
  | 'casino'
  | 'sheriff'
  | 'school'
  | 'hospital'
  | 'communityHall'
  | 'chapel'
  | 'townHall'
  | 'bank'
  | 'park'
  | 'publicPool'
  | 'university'
  | 'railDepot'
  | 'oreDepot'
  | 'generator'
  | 'powerPole'
  | 'waterTank'
  | 'well'
  | 'cornFarm'
  | 'wheatFarm'
  | 'cattleRanch'
  | 'warehouse'
  | 'freightOffice'
  | 'goodsMarket'
  | 'glassworks'
  | 'chipFab'
  | 'solarArray'
  | 'nuclearPlant'
  | 'gridInterconnect'
  | 'passengerTerminal';

export interface Building {
  id: number;
  type: BuildingType;
  /** Footprint origin; buildings sit on depth-0 columns only in v0.1. */
  x: number;
  y: number;
  staffAssigned: number[];
  /** Chain-input stockpile (ranch: corn, fab: glass); 0 for everything else.
   * The good is implied by the def's `consumes`. */
  inputUnits: number;
  /** Internal output store for `produces` buildings — the barn. Production
   * fills this first; perimeter piles are overflow only. Haulers draw from
   * it, so an enclosed farm keeps farming until the store is full. */
  outputUnits: number;
  /** 0/1 lock: a hauler is en route to collect (mirrors pile claiming). */
  outputClaimedUnits: number;
  /** Pathfinding found no route to this building's output; cleared when the
   * terrain version changes (same contract as DigOrder.suspended). */
  outputSuspended: boolean;
  /** Nuclear scram: supplies no power until this day. */
  outageUntilDay: number | null;
  // Derived on network rebuild / daily tick; recomputed on load.
  powered: boolean;
  powerEff: number;
  watered: boolean;
  functional: boolean;
}

export interface BuildingDef {
  costCents: number;
  w: number;
  h: number;
  beds?: number;
  /** Number of family households this building houses (cottage 1 ... apartment 6). */
  familyHomes?: number;
  /** Home extra: happiness bonus for households living here. */
  homeComfort?: number;
  /** Weekly rent PER household living here. */
  rentCentsPerWeek?: number;
  /** Contribution to town attractiveness when functional (0..1 scale points). */
  attract?: number;
  foodCap?: number;
  drinkCap?: number;
  gambleCap?: number;
  /** Civic service provided when functional: education/health/community. */
  service?: 'education' | 'health' | 'community';
  /** How many people this service building realistically serves (education
   * counts children; health/community count everyone). Needs degrade softly
   * as demand exceeds the town's total functional capacity. */
  serviceCap?: number;
  staffRequired: number;
  powerDraw: number;
  powerSupply?: number;
  waterPerDay?: number;
  waterCapacity?: number;
  wellYieldPerDay?: number;
  fuelCentsPerWeek?: number;
  /** Staff here draw full tier wages (industry jobs), unlike amenity staff. */
  staffWaged?: true;
  /** Daily goods output while functional and unblocked. */
  produces?: { good: GoodKind; unitsPerDay: number };
  /** Internal output store cap; defaults to 4 days of production. */
  outputCapUnits?: number;
  /** Chain input: production hard-stops when the buffer runs dry. */
  consumes?: { good: GoodKind; unitsPerDay: number; bufferUnits: number };
  /** Warehouse: adds this much shared goods-depot capacity. */
  goodsCapacityUnits?: number;
  /** Local retail: sells stored goods to residents weekly at a premium. */
  retail?: true;
  /** Reserved right-of-way row to a map edge (railDepot west, interconnect east). */
  corridor?: 'west' | 'east';
  /** Sidebar tooltip line while locked (defaults to a generic hint). */
  lockHint?: string;
  /** Letter id that must have been delivered before this can be built. */
  unlockedBy?: string;
  label: string;
}

export type PeepJob = 'miner' | 'hauler' | 'staff' | 'industry' | 'idle';

/** 0 Transient, 1 Regular, 2 Veteran, 3 Settler (has a family in town). */
export type Tier = 0 | 1 | 2 | 3;

export type PeepRole = 'worker' | 'spouse' | 'child';

export interface Household {
  id: number;
  /** null = resident household drawn by the town itself (no mine worker). */
  workerId: number | null;
  spouseId: number;
  /** Resident households may have a second adult instead of a worker. */
  secondAdultId?: number | null;
  childIds: number[];
  homeBuildingId: number;
  /** 0..1, recomputed daily from services + dust at the home site. */
  happiness: number;
  dust: number;
}

export interface FamilyRequest {
  id: number;
  peepId: number;
  workerName: string;
  day: number;
  expiresDay: number;
  status: 'pending' | 'approved' | 'declined' | 'expired';
}

export type PeepTask =
  | { kind: 'idle' }
  | { kind: 'toDig'; col: Col }
  | { kind: 'digging'; col: Col; ticksLeft: number }
  | { kind: 'toPile'; col: Col }
  | { kind: 'toDepot' }
  | { kind: 'toGoodsPile'; col: Col }
  | { kind: 'toOutputPickup'; buildingId: number }
  | { kind: 'toGoodsDropoff'; buildingId: number }
  | { kind: 'staffing'; buildingId: number }
  | { kind: 'offDuty'; untilDay: number };

export interface Peep {
  id: number;
  name: string;
  job: PeepJob;
  role: PeepRole;
  tier: Tier;
  /** Weeks of service that counted toward promotion (morale-gated). */
  tenureWeeks: number;
  householdId: number | null;
  /** Children only: the week they come of age and join the workforce. */
  growsUpWeek: number | null;
  /** Workers only: never re-ask after a declined family request. */
  familyAskedWeek: number | null;
  x: number;
  y: number;
  /** Previous-tick position, for render interpolation. */
  px: number;
  py: number;
  path: Col[] | null;
  pathIdx: number;
  pathTerrainVersion: number;
  repathFailures: number;
  task: PeepTask;
  /** Cargo quantity — decitons of ore OR units of goods, per carryGood. */
  carryDt: number;
  carryType: OreType;
  /** Non-null while hauling goods; carryDt is then units, not decitons. */
  carryGood: GoodKind | null;
}

export interface WorkforceNeeds {
  housing: number;
  food: number;
  water: number;
  drink: number;
  gambling: number;
}

export interface FamilyNeeds {
  education: number;
  health: number;
  community: number;
  /** 1 = clean air, 0 = homes buried in pit dust. */
  air: number;
}

export interface Workforce {
  needs: WorkforceNeeds;
  familyNeeds: FamilyNeeds;
  unlocked: { drink: boolean; gambling: boolean };
  morale: number;
  paidOnTime: number;
  payrollMissedStreak: number;
  weeklyQuits: number;
  weeklyHires: number;
  hiringOpen: boolean;
  /** Named family requests already sent (count drives the hybrid arrival switch). */
  familyRequestsSent: number;
}

export interface PriceState {
  /** Cents per ton. */
  price: number;
  mean: number;
  spikeWeeksLeft: number;
}

export type LedgerKind =
  | 'sale'
  | 'payroll'
  | 'rent'
  | 'amenity'
  | 'fuel'
  | 'water'
  | 'build'
  | 'incident'
  | 'interest'
  | 'bailout'
  | 'reward'
  | 'severance';

export interface LedgerEntry {
  day: number;
  kind: LedgerKind;
  amountCents: number;
  note: string;
}

/** A price shock on one commodity, announced by letter/toast. */
export interface MarketEvent {
  good: GoodKind | 'iron' | 'gold';
  kind: 'boom' | 'bust';
  weeksLeft: number;
}

export interface Economy {
  cashCents: number;
  debtCents: number;
  bailoutsUsed: number;
  market: {
    iron: PriceState;
    gold: PriceState;
    corn: PriceState;
    wheat: PriceState;
    cattle: PriceState;
    glass: PriceState;
    chips: PriceState;
    /** Cents per unit of surplus supply sold through the interconnect weekly. */
    power: PriceState;
  };
  depot: { ironDt: number; goldDt: number; capacityDt: number };
  truck: {
    state: 'idle' | 'selling';
    returnsOnDay: number;
    cargoValueCents: number;
  };
  /** Shared goods stockpile; capacity comes from functional warehouses. */
  goodsDepot: { units: Record<GoodKind, number>; capacityUnits: number };
  goodsTruck: {
    state: 'idle' | 'selling';
    returnsOnDay: number;
    cargoValueCents: number;
  };
  marketEvent: MarketEvent | null;
  /** Capped ring, newest last. */
  ledger: LedgerEntry[];
  /** Severance paid out since the last payroll — reported (not re-charged)
   * on the next weekly settlement. */
  weekSeveranceCents: number;
}

export interface WaterState {
  tankLevelUnits: number;
  tankCapacity: number;
  autoRefill: boolean;
  refillArrivesOnDay: number | null;
  /** Yesterday's demand actually met (0..1) — feeds the water need. */
  supplyRatio: number;
}

export interface LetterInstance {
  defId: string;
  deliveredDay: number;
  read: boolean;
}

export interface QuotaState {
  oreType: OreType;
  amountDt: number;
  deliveredDt: number;
  byWeek: number;
  rewardCents: number;
  completed: boolean;
  failed: boolean;
}

export interface LettersState {
  delivered: LetterInstance[];
  quota: QuotaState | null;
}

export type IncidentKind = 'brawl' | 'theft' | 'noShow' | 'scram';

export interface IncidentsState {
  lastIncidentDay: number;
  log: { day: number; kind: IncidentKind; note: string }[];
}

export type Speed = 0 | 1 | 3 | 8;

export type Era = 'company' | 'pullout' | 'installments' | 'independent';

export type EndingKind = 'endures' | 'strip' | 'ghost' | 'foreclosed';

export interface LegacyBreakdown {
  fortune: number;
  people: number;
  selfSufficiency: number;
  townQuality: number;
  companyRecord: number;
  total: number;
  title: string;
}

export interface EndingRecord {
  kind: EndingKind;
  week: number;
  townName: string;
  legacy: LegacyBreakdown;
}

export interface TownState {
  /** 0..1 — drives commerce, rail income, immigration. */
  attractiveness: number;
  endureStreak: number;
  insolventStreak: number;
  /** Weeks since the mine stopped mattering (mine income < 20% of total). */
  postOreWeeks: number;
  oreMinedTotalDt: number;
  quotasCompleted: number;
  /** Trailing weekly mine income samples (latest last, max 4). */
  mineIncomeHistory: number[];
  /** Trailing weekly town income (tax+commerce+rail+rent). */
  townIncomeHistory: number[];
  /** Current-week accumulator: ore sale settlements. */
  weekMineIncomeCents: number;
  /** Current-week accumulator: goods truck settlements. Rolls into the town
   * history — a farm town's income is town income, not mine income. */
  weekGoodsIncomeCents: number;
}

export interface InstallmentPlan {
  totalCents: number;
  weeklyCents: number;
  paidWeeks: number;
  totalWeeks: number;
  missedWeeks: number;
}

export interface Meta {
  seed: number;
  tick: number;
  speed: Speed;
  gameOver: boolean;
  gameOverReason: string | null;
  nextEntityId: number;
  townName: string | null;
  era: Era;
  /** Week the pull-out letter arrived; null before it. */
  pulloutWeek: number | null;
  installments: InstallmentPlan | null;
  ending: EndingRecord | null;
}

export interface SimState {
  meta: Meta;
  terrain: Terrain;
  orders: Map<Col, DigOrder>;
  piles: Map<Col, OrePile>;
  goodsPiles: Map<Col, GoodsPile>;
  buildings: Building[];
  peeps: Peep[];
  households: Household[];
  familyRequests: FamilyRequest[];
  workforce: Workforce;
  economy: Economy;
  water: WaterState;
  town: TownState;
  letters: LettersState;
  incidents: IncidentsState;
  /** Requested miner/hauler split; sim reassigns idle-first. */
  jobTargets: { miners: number; haulers: number };
}

export interface PayrollBreakdown {
  /** All tier wages paid — mine and industry jobs alike. */
  wages: number;
  /** Of which industry staff — informational split, already inside wages. */
  industryWages: number;
  rent: number;
  food: number;
  saloon: number;
  casino: number;
  fuel: number;
  interest: number;
  /** Severance already paid at dismissal this week — informational, not in net. */
  severance: number;
  /** Incorporated-town income: taxes + commerce + rail + bank + retail +
   * power exports + passenger fares. */
  town: number;
  /** Goods-truck settlements banked this week — town income, shown apart. */
  goodsSales: number;
  /** Ore-truck settlements banked this week — mine income, shown apart. */
  oreSales: number;
  /** Of `town`: electricity sold to the grid. */
  powerSales: number;
  /** Of `town`: passenger rail fares. */
  passengerFares: number;
  net: number;
  missed: boolean;
}

export type SimEvent =
  | { kind: 'letterDelivered'; defId: string }
  | { kind: 'incident'; incident: IncidentKind; note: string }
  | { kind: 'sale'; amountCents: number; note: string }
  | { kind: 'payroll'; breakdown: PayrollBreakdown }
  | { kind: 'brownout'; buildingId: number }
  | { kind: 'hire'; count: number }
  | { kind: 'quit'; count: number }
  | { kind: 'dismissed'; count: number; severanceCents: number }
  | { kind: 'promotion'; peepName: string; tier: Tier }
  | { kind: 'familyRequest'; requestId: number; workerName: string }
  | { kind: 'familySettled'; workerName: string; auto: boolean }
  | { kind: 'kidGrewUp'; name: string }
  | { kind: 'incorporated'; townName: string }
  | { kind: 'pulloutOffered' }
  | { kind: 'independence' }
  | { kind: 'endingReached'; ending: EndingKind }
  | { kind: 'residentArrived'; surname: string }
  | { kind: 'truckDeparted'; cargoValueCents: number }
  | { kind: 'goodsTruckDeparted'; cargoValueCents: number }
  | { kind: 'marketEvent'; good: string; boom: boolean }
  | { kind: 'scram'; buildingId: number }
  | { kind: 'bankruptcy'; final: boolean }
  | { kind: 'tileChanged'; col: Col }
  | { kind: 'buildingsChanged' }
  | { kind: 'quotaCompleted' }
  | { kind: 'toast'; message: string };
